import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Variants,
} from 'framer-motion'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { EASE_OUT } from '@shared/animations/consts'

type Direction = 1 | -1
type Variant = 'slide' | 'morph'

const SLIDE_PX = 16
const FADE_DURATION = 0.12
const MOVE_DURATION = 0.18
const HEIGHT_DURATION = 0.18

export type AnimatedSwitchProps = {
  activeKey: React.Key
  activeIndex?: number
  wrapperClassName?: string
  contentClassName?: string
  children: React.ReactNode
  variant?: Variant
}

export const AnimatedSwitch: React.FC<AnimatedSwitchProps> = ({
  activeKey,
  activeIndex,
  wrapperClassName,
  contentClassName,
  children,
  variant = 'slide',
}) => {
  const prefersReducedMotion = useReducedMotion()

  const prevIndexRef = useRef<number>(activeIndex ?? null)
  const directionRef = useRef<Direction>(1)
  const prevActiveKeyRef = useRef<React.Key>(null)
  const hasMountedRef = useRef(false)

  const [lockedHeight, setLockedHeight] = useState<number | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(null)

  const phaseRef = useRef<'idle' | 'locked-current' | 'animating-to-next'>('idle')
  const lockedHeightRef = useRef<number>(null)

  const direction: Direction = (() => {
    if (typeof activeIndex !== 'number') {
      return directionRef.current
    }
    const prev = prevIndexRef.current

    if (typeof prev !== 'number' || activeIndex === prev) {
      return directionRef.current
    }

    return activeIndex > prev ? 1 : -1
  })()

  useLayoutEffect(() => {
    directionRef.current = direction

    if (typeof activeIndex === 'number') {
      prevIndexRef.current = activeIndex
    }
  }, [activeIndex, direction])

  const measureContentHeight = () => {
    const el = contentRef.current
    if (!el) return null
    return Math.ceil(el.getBoundingClientRect().height)
  }

  useLayoutEffect(() => {
    if (prefersReducedMotion) {
      phaseRef.current = 'idle'
      lockedHeightRef.current = null
      setLockedHeight(null)
      prevActiveKeyRef.current = activeKey
      hasMountedRef.current = true
      return
    }

    const isFirstRender = !hasMountedRef.current
    const didKeyChange = prevActiveKeyRef.current !== null && prevActiveKeyRef.current !== activeKey

    prevActiveKeyRef.current = activeKey
    hasMountedRef.current = true

    if (isFirstRender || !didKeyChange) {
      phaseRef.current = 'idle'
      lockedHeightRef.current = null
      setLockedHeight(null)
      return
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current)

    const current = measureContentHeight()
    if (current == null) return

    phaseRef.current = 'locked-current'
    lockedHeightRef.current = current
    setLockedHeight(current)
  }, [activeKey, prefersReducedMotion])

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  const slide = prefersReducedMotion ? 0 : SLIDE_PX
  const heightDuration = prefersReducedMotion ? 0 : HEIGHT_DURATION

  const motionVariants: Variants =
    variant === 'morph'
      ? {
          initial: { opacity: 0, scale: 0.985 },
          animate: { opacity: 1, scale: 1 },
          exit: {
            opacity: 0,
            scale: 0.985,
            position: 'absolute',
            inset: 0,
            width: '100%',
          },
        }
      : {
          initial: (dir: Direction) => ({ opacity: 0, x: slide * dir }),
          animate: { opacity: 1, x: 0 },
          exit: (dir: Direction) => ({
            opacity: 0,
            x: -slide * dir,
            position: 'absolute',
            inset: 0,
            width: '100%',
          }),
        }
  const motionTransition: HTMLMotionProps<'div'>['transition'] =
    variant === 'morph'
      ? {
          opacity: { duration: prefersReducedMotion ? 0 : FADE_DURATION, ease: 'easeOut' },
          scale: { duration: prefersReducedMotion ? 0 : MOVE_DURATION, ease: EASE_OUT },
        }
      : {
          opacity: { duration: prefersReducedMotion ? 0 : FADE_DURATION, ease: 'easeOut' },
          x: { duration: prefersReducedMotion ? 0 : MOVE_DURATION, ease: EASE_OUT },
        }

  return (
    <motion.div
      className={wrapperClassName}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        height: lockedHeight == null ? 'auto' : lockedHeight,
      }}
      animate={lockedHeight == null ? {} : { height: lockedHeight }}
      transition={{ duration: heightDuration, ease: EASE_OUT }}
      onAnimationComplete={() => {
        if (phaseRef.current === 'animating-to-next') {
          phaseRef.current = 'idle'
          lockedHeightRef.current = null
          setLockedHeight(null)
        }
      }}
    >
      <AnimatePresence
        initial={false}
        mode='wait'
        custom={direction}
        onExitComplete={() => {
          if (prefersReducedMotion) return

          if (rafRef.current) cancelAnimationFrame(rafRef.current)

          rafRef.current = requestAnimationFrame(() => {
            const next = measureContentHeight()
            if (next == null) return

            const prevLocked = lockedHeightRef.current
            if (prevLocked != null && next === prevLocked) {
              phaseRef.current = 'idle'
              lockedHeightRef.current = null
              setLockedHeight(null)
              return
            }

            phaseRef.current = 'animating-to-next'
            lockedHeightRef.current = next
            setLockedHeight(next)
          })
        }}
      >
        <motion.div
          key={String(activeKey)}
          custom={direction}
          variants={motionVariants}
          initial='initial'
          animate='animate'
          exit='exit'
          transition={motionTransition}
          style={{
            width: '100%',
            willChange: 'transform, opacity',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          }}
        >
          <div ref={contentRef} className={contentClassName} style={{ width: '100%' }}>
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}
